/**
 * components/settings/SettingsStampSection.tsx
 *
 * Sous-section du profil dédiée à la configuration du tampon circulaire.
 * Affiche deux champs de saisie (intitulé et numéro de toque) et génère
 * un aperçu en temps réel du tampon SVG correspondant.
 * Intégrée dans la card de SettingsProfileSection, sous la section logo.
 * Updated: ajout de sectionRef + id="stamp" pour le scroll automatique depuis la modale
 * dossier de pièces (lien /settings#stamp).
 */

import { type RefObject } from 'react';
import Input from '../ui/Input';
import StampPreview from './StampPreview';
import { ProfileData } from '../../hooks/useSettingsProfile';

interface SettingsStampSectionProps {
  profile: ProfileData;
  onUpdateField: (field: keyof Omit<ProfileData, 'email' | 'created_at'>, value: string) => void;
  sectionRef?: RefObject<HTMLDivElement>;
}

export default function SettingsStampSection({
  profile,
  onUpdateField,
  sectionRef,
}: SettingsStampSectionProps) {
  return (
    <div ref={sectionRef} id="stamp" className="border-t border-neutral-100 pt-4">
      <label className="block text-sm font-medium text-neutral-700 mb-1">
        Tampon
      </label>
      <p className="text-xs text-neutral-500 mb-4">
        Renseignez l'intitulé et le numéro de toque pour générer automatiquement votre tampon.
      </p>

      <div className="flex flex-col sm:flex-row gap-6 items-start">
        <div className="flex-1 space-y-3 min-w-0">
          <Input
            label="Intitulé"
            value={profile.stamp_title}
            onChange={(e) => onUpdateField('stamp_title', e.target.value)}
            autoComplete="organization"
          />
          <Input
            label="Numéro de toque"
            value={profile.stamp_toque_number}
            onChange={(e) => onUpdateField('stamp_toque_number', e.target.value)}
          />
        </div>

        <div className="flex-shrink-0 flex flex-col items-center gap-1">
          <StampPreview
            title={profile.stamp_title}
            toqueNumber={profile.stamp_toque_number}
            size={150}
          />
          <span className="text-xs text-neutral-400">Aperçu</span>
        </div>
      </div>
    </div>
  );
}
